import { useLiveQuery } from 'dexie-react-hooks'
import { cycleReport } from '../db/budget'
import { cycleLabel, cycleLabelDefined } from '../lib/cycles'
import { formatAriary, formatSigned } from '../lib/money'
import { Icon } from './Icon'
import './CycleReportSheet.css'

/**
 * Le bilan d'un cycle — feature 3.
 *
 * Une feuille et non une cinquième destination : la navigation est arrêtée à
 * quatre (décision fermée), et le résumé se lit là où l'on se pose la question.
 *
 * La COMPARAISON est le cœur. « 320 000 dépensés » ne dit rien tout seul ;
 * « 320 000, soit 40 000 de plus que le mois dernier » dit quelque chose sur
 * lequel on peut agir.
 */
export function CycleReportSheet(
  { walletId, date, onClose }: { walletId: string; date: string; onClose: () => void },
) {
  const report = useLiveQuery(() => cycleReport(walletId, date), [walletId, date], undefined)

  if (!report) return null

  const ecart = report.expense - report.previousExpense
  const parJour = report.activeDays > 0 ? Math.round(report.expense / report.activeDays) : 0

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label={`Résumé ${cycleLabelDefined(report.range, report.cycle)}`}>
      <div className="sheet report-sheet">
        <header className="sheet-head">
          <h2>Résumé · {cycleLabel(report.range, report.cycle)}</h2>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Fermer">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="report-body">
          <div className="report-figures">
            <p><em>Reçu</em><strong className="tabular is-income">
              {formatSigned(report.income, 'income')}</strong></p>
            <p><em>Dépensé</em><strong className="tabular is-expense">
              {formatSigned(report.expense, 'expense')}</strong></p>
            <p>
              <em>{report.net >= 0 ? 'Il te reste' : 'Tu as puisé'}</em>
              <strong className={`tabular ${report.net < 0 ? 'is-expense' : ''}`}>
                {formatAriary(Math.abs(report.net))}
              </strong>
            </p>
          </div>

          {/* Le seul chiffre qui dise quelque chose d'actionnable. */}
          {report.previousExpense > 0 && (
            <p className={`report-trend ${ecart > 0 ? 'is-up' : 'is-down'}`}>
              <Icon name={ecart > 0 ? 'arrowUp' : 'arrowDown'} size={14} />
              {ecart === 0
                ? 'Exactement autant que la période précédente.'
                : <>
                    <strong className="tabular">{formatAriary(Math.abs(ecart))}</strong>
                    {ecart > 0 ? ' de plus' : ' de moins'} que la période précédente
                  </>}
            </p>
          )}

          {report.activeDays > 0 && (
            <p className="report-note">
              {report.activeDays} jour{report.activeDays > 1 ? 's' : ''} avec une dépense,
              soit <strong className="tabular">{formatAriary(parJour)}</strong> par jour
              de dépense.
            </p>
          )}

          {report.savedOnGoals > 0 && (
            <p className="report-note">
              <strong className="tabular">{formatAriary(report.savedOnGoals)}</strong> mis
              de côté sur tes objectifs.
            </p>
          )}

          {report.lines.length === 0 ? (
            <p className="report-note">Aucune dépense sur cette période.</p>
          ) : (
            <>
              <h3>Où l'argent est parti</h3>
              <ul className="report-lines">
                {report.lines.map((line) => (
                  <li key={line.category_id ?? 'aucune'}>
                    <div className="report-line-head">
                      <span>{line.name}</span>
                      <strong className="tabular">{formatAriary(line.amount)}</strong>
                    </div>
                    <div className="report-bar" aria-hidden="true">
                      <span style={{ inlineSize: `${line.share * 100}%` }} />
                    </div>
                    {/* Le budget en regard : sans lui, « 120 000 en nourriture »
                        ne dit pas si c'est beaucoup. */}
                    {line.budgeted !== null && line.budgeted > 0 && (
                      <p className={`report-vs ${line.amount > line.budgeted ? 'is-over' : ''}`}>
                        {line.amount > line.budgeted
                          ? <>dépassé de <strong className="tabular">
                              {formatAriary(line.amount - line.budgeted)}</strong></>
                          : <>sur <strong className="tabular">
                              {formatAriary(line.budgeted)}</strong> prévus</>}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
